import { useEffect, useState } from 'react';
import { BsCreditCard2FrontFill } from 'react-icons/bs';
import { Row, Col } from 'react-bootstrap';
import { ModalGeneric, SelectGeneric, Loader } from 'ypf-pas-general-ui-components';
import { format, add } from 'date-fns';
import PropTypes from 'prop-types';

import AccountAssignmentObjectDetail from '../../Wells/AccountAssignmentObjectDetail/AccountAssignmentObjectDetail';
import HeaderDetail from '../../Wells/HeaderDetail/HeaderDetail';
import { getDaysInterval } from '../../WeeklyReportTable/utils/date';
import { reverseFormatAmountDate, formatAmountDateBack } from '../../../utils/formatUtils';
import { postImputation, putImputation } from '../../../services/workReport';
import { mapperImputationDefault } from '../../../utils/mapper';
import useToast from '../../../hooks/useToast';
import {
  getImputationType,
  getImputationsObjects,
  getBudgetService,
  getImputationsDetails
} from '../../../services/yclickMaster';
import { imputationTypeEnum } from '../../../utils/dataStatus';
import { useWorkReportModals } from '../../../context/WorkReportModalsContext';

import styles from './ImputationModal.module.scss';

const ImputationModal = ({ onAccept, onHide, show, ...props }) => {
  const {
    dailyWorkReport = {
      editaImputacion: false,
      fecha: '',
      lineaSap: '',
      posicionSap: '',
      ubicacion: '',
      idParteDiario: 0,
      weekly: false
    }
  } = props;

  const [loading, setLoading] = useState(false);
  const [initalImputationType, setInitalImputationType] = useState(null);
  const [imputationType, setImputationType] = useState(null);
  const [imputationTypeOptions, setImputationTypeOptions] = useState([]);
  const [initialImputationObject, setInitalImputationObject] = useState(null);
  const [imputationObject, setImputationObject] = useState(null);
  const [objectDetail, setObjectDetail] = useState();
  const [isSameValue, setIsSameValue] = useState(true);
  const [noOptions, setNoOptions] = useState('Ingresar al menos 5 caracteres');
  const { activeNewValueImputation } = useWorkReportModals();
  const { setNewToast } = useToast();

  const {
    editaImputacion,
    fecha = '',
    lineaSap,
    posicionSap,
    ubicacion,
    weekly,
    idParteDiario
  } = dailyWorkReport;

  const positionNumber = posicionSap.split(' - ')[0];
  const lineNumber = lineaSap.split(' - ')[0];
  const date = fecha ? new Date(fecha) : new Date();

  const isWeekly = weekly
    ? getDaysInterval({
        start: date,
        end: add(date, { days: 6 }),
        dateFormat: 'dd/MM/yyyy'
      })
    : false;

  // Esta estructura es exclusivamente para no modificar las props del
  // componente y renderizar los datos que se necesitan en este modal.
  const data = {
    well: { label: ubicacion },
    event: { sigla: `P${positionNumber} - L${lineNumber}` },
    team: isWeekly
      ? { label: `${format(date, 'dd/MM/yyyy')} al ${isWeekly[6]}` }
      : { label: format(date, 'dd/MM/yyyy') }
  };

  const imputacionRequest = {
    idsParteDiario: isWeekly ? idParteDiario : [idParteDiario],
    fechaRealizacionDesde: isWeekly
      ? reverseFormatAmountDate(isWeekly[0])
      : formatAmountDateBack(fecha),
    fechaRealizacionHasta: isWeekly
      ? reverseFormatAmountDate(isWeekly[6])
      : formatAmountDateBack(fecha),
    nroPosicion: positionNumber,
    nroLinea: lineNumber
  };

  const formIsAlreadyEdited =
    initialImputationObject?.id !== imputationObject?.id ||
    initalImputationType?.id !== imputationType?.id;

  const handleChangeImputation = async () => {
    try {
      setLoading(true);

      const res = await putImputation(imputacionRequest, imputationType?.id, imputationObject?.id);

      setNewToast({
        type: 'SUCCESS',
        message: res.message
      });

      onHide();
      onAccept();
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
    }
  };

  const handlePrimaryAction = async () => {
    if (weekly) {
      onHide();
      activeNewValueImputation({
        onAccept: () => handleChangeImputation()
      });
    } else {
      handleChangeImputation();
    }
  };

  const handleOnHide = () => {
    onHide();
  };

  const adaptData = async (dataToAdapt) => {
    let budget = {};

    const details = mapperImputationDefault(
      dataToAdapt.detalleObjeto,
      dataToAdapt.tipoObjetoImputacion
    );

    if (dataToAdapt.tipoObjetoImputacion.id === imputationTypeEnum.PEP) {
      budget = await getBudgetService(dataToAdapt.objetoImputacion.id);
    }

    return {
      ...details,
      budget: budget?.presupuesto || null,
      warningBudget: (budget && budget?.presupuesto >= 10000) || false
    };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const options = await getImputationType();

      setImputationTypeOptions(options);
      const res = await postImputation(imputacionRequest);

      const differentValues = () => {
        const mappedImputationType = {
          id: 0,
          nombre: null,
          descripcion: null,
          value: 0,
          label: 'Varios valores'
        };
        const mappedImputationObject = {
          id: 0,
          nombre: null,
          descripcion: null,
          value: 0,
          label: 'Varios valores'
        };

        setInitalImputationType(mappedImputationType);
        setImputationType(mappedImputationType);
        setInitalImputationObject(mappedImputationObject);
        setImputationObject(mappedImputationObject);
        setIsSameValue(false);
      };

      if (res.data.length) {
        // Compara los datos si son del mismo valor o hay diferencias
        const compareIdsImputationObject = (value) =>
          value.objetoImputacion.id === res.data[0].objetoImputacion.id;
        const sameImputationObject = res.data.every(compareIdsImputationObject);

        if (sameImputationObject) {
          const dataImputation = res.data[0];

          const resAdapted = await adaptData(dataImputation);

          setObjectDetail(resAdapted);
          const mappedImputationType = {
            id: dataImputation.tipoObjetoImputacion.id,
            nombre: dataImputation.tipoObjetoImputacion.nombre,
            descripcion: null,
            value: dataImputation.tipoObjetoImputacion.id,
            label: dataImputation.tipoObjetoImputacion.nombre
          };
          const mappedImputationObject = {
            id: dataImputation.objetoImputacion.id,
            nombre: dataImputation.objetoImputacion.nombre,
            descripcion: dataImputation.objetoImputacion.descripcion,
            value: dataImputation.objetoImputacion.id,
            label: `${dataImputation.objetoImputacion.nombre} | ${dataImputation.objetoImputacion.descripcion}`
          };

          setImputationType(mappedImputationType);
          setInitalImputationType(mappedImputationType);
          setImputationObject(mappedImputationObject);
          setInitalImputationObject(mappedImputationObject);
        } else {
          differentValues();
        }
      }

      if (
        res.message ===
        'El Objeto de Imputación tiene varios valores. Al confirmar un nuevo valor, se aplicará sobre los anteriores.'
      ) {
        differentValues();
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
    }
  };

  const fetchDataObjects = async (type, searchValue) => {
    if (searchValue.length < 5) {
      setNoOptions('Ingresar al menos 5 caracteres');

      return null;
    }
    try {
      const objectResponse = await getImputationsObjects(type.id, searchValue);

      setNoOptions('Sin opciones');

      return objectResponse.map((res) => ({
        id: res.id,
        nombre: res.codigo && res.descripcion ? `${res.codigo} | ${res.descripcion}` : undefined
      }));
    } catch (err) {
      return null;
    }
  };

  const fetchPreviewDetail = async (object) => {
    try {
      if (!object) {
        setImputationObject({});

        return;
      }
      setLoading(true);
      const res = await getImputationsDetails([
        {
          tipoId: imputationType.id,
          objetoId: object.id
        }
      ]);

      const dataToAdapt = {
        detalleObjeto: res[0],
        objetoImputacion: object,
        tipoObjetoImputacion: imputationType
      };

      const resAdapted = await adaptData(dataToAdapt);

      setObjectDetail(resAdapted);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
    }
  };

  useEffect(() => {
    if (show) {
      fetchData();
    }
    if (!show) {
      setImputationType(null);
      setImputationTypeOptions([]);
      setImputationObject(null);
      setObjectDetail();
      setIsSameValue(true);
    }
    // eslint-disable-next-line
  }, [show]);

  return (
    <>
      <ModalGeneric
        backdrop="static"
        infoModal
        onHide={handleOnHide}
        primaryAction={handlePrimaryAction}
        primaryActionDisabled={
          !editaImputacion ||
          !imputationObject?.value ||
          imputationObject?.label === 'Varios valores' ||
          !formIsAlreadyEdited
        }
        primaryActionLabel="Aceptar"
        secondaryAction={handleOnHide}
        secondaryActionLabel="Salir"
        show={show}
        size="xl"
        title="Detalle del tipo y objeto de imputación"
      >
        <HeaderDetail data={data} disabledDeleteWell />
        {!isSameValue && (
          <div className={styles['warning-container']}>
            <BsCreditCard2FrontFill className={styles['warning-icon']} />
            <p className={styles['warning-message']}>
              El Objeto de Imputación tiene varios valores. Al confirmar un nuevo valor, se aplicará
              sobre los anteriores.
            </p>
          </div>
        )}
        <div className={styles['imputation-container']}>
          <Row>
            <Col sm={3}>
              <SelectGeneric
                isDisabled={!editaImputacion}
                label="Tipo de imputación *"
                name="imputationType"
                onChange={(newValue) => {
                  setImputationType(newValue);
                  setImputationObject(null);
                }}
                options={imputationTypeOptions}
                paddingIndicator={4}
                placeholder="Tipo imputación"
                value={imputationType}
                withSelect="100%"
              />
            </Col>
            <Col sm={9}>
              <SelectGeneric
                async
                fetchData={(searchValue) => fetchDataObjects(imputationType, searchValue)}
                isDisabled={
                  !editaImputacion || !imputationType || imputationType?.label === 'Varios valores'
                }
                label="Objeto de imputación | Denominación objeto *"
                name="imputationObject"
                noOptionsMessage={noOptions}
                onChange={(newValue) => {
                  // Se agrega if por que si se apreta backspace sin haber un valor, llama al onChange y manda null
                  setImputationObject(newValue);
                  fetchPreviewDetail(newValue);
                }}
                paddingIndicator={4}
                placeholder="Buscar por nombre"
                value={imputationObject}
                withSelect="100%"
              />
            </Col>
          </Row>
          <AccountAssignmentObjectDetail data={objectDetail} societyLabel={false} />
        </div>
        <Loader show={loading} text="CARGANDO" />
      </ModalGeneric>
    </>
  );
};

export default ImputationModal;

ImputationModal.propTypes = {
  dailyWorkReport: PropTypes.shape({
    fecha: PropTypes.string,
    lineaSap: PropTypes.string,
    posicionSap: PropTypes.string,
    ubicacion: PropTypes.string,
    weekly: PropTypes.bool
  }).isRequired,
  onAccept: PropTypes.func,
  onHide: PropTypes.func,
  show: PropTypes.bool.isRequired
};

ImputationModal.defaultProps = {
  onAccept: () => {},
  onHide: () => {}
};
